import { useState, useCallback } from 'react';
import { TRANSLATIONS, getTranslationGroups, saveTranslationOverrides, loadTranslationOverrides, getEffectiveTranslations, SUPPORTED_LANGS } from '../utils/i18n';
import { CMS_PAGES, loadPageContent, savePageContent, resetPageContent } from '../utils/cms';
import { BASE_PAGES } from '../pages/StaticPage';

// ── Admin light-theme palette ─────────────────────────────────────────────
const G = {
  bg:      '#F4F2EF',
  card:    '#FFFFFF',
  border:  '#E5E2DC',
  borderMd:'#D0CCC4',
  text:    '#1A1714',
  white:   '#1A1714',   // alias kept so old refs still work
  sub:     '#5C5650',
  muted:   '#8C8680',
  gold:    '#C9A84C',
  goldBg:  'rgba(201,168,76,0.08)',
  goldBdr: 'rgba(201,168,76,0.35)',
  blue:    '#5B7FC4',
  green:   '#1A7A4A',
  greenBg: '#E8F5EE',
  red:     '#C0392B',
  sans:    "'Outfit',sans-serif",
  serif:   "'Cormorant Garamond',serif",
};

// ── GROUP LABELS ──────────────────────────────────────────────────────────
const GROUP_LABELS = {
  nav: 'Navigation', common: 'Common buttons', auth: 'Sign in / Sign up',
  discover: 'Discover', profile_view: "Other's profile", my_profile: 'My profile',
  settings: 'Settings', invite: 'Invite & referral', matches: 'Matches',
  messages: 'Messages', hive: 'BeeHive / La Ruche', events: 'Events',
  report: 'Report & Block', completion: 'Profile completion', upgrade: 'Upgrade & Plans',
  badges: 'Badges', beewise: 'Bee Wise', spotify: 'Spotify', landing: 'Landing page',
};

// ────────────────────────────────────────────────────────────────────────────
// TRANSLATIONS TAB
// ────────────────────────────────────────────────────────────────────────────
export function TranslationsTab() {
  const [activeGroup, setActiveGroup] = useState('all');
  const [filter, setFilter]           = useState('all');
  const [search, setSearch]           = useState('');
  const [editsEN, setEditsEN] = useState(() => loadTranslationOverrides('en') || {});
  const [editsFR, setEditsFR] = useState(() => loadTranslationOverrides('fr') || {});
  const [saved, setSaved]     = useState(false);
  const [editingKey, setEditingKey] = useState(null);

  const groups   = getTranslationGroups('en');
  const groupsFR = getTranslationGroups('fr');

  const allPairs = Object.values(groups).flat().map(enPair => {
    const frPair = Object.values(groupsFR).flat().find(p => p.key === enPair.key);
    return {
      key:      enPair.key,
      group:    enPair.group,
      en:       enPair.value || enPair.en || enPair.key,
      fr:       frPair?.value || frPair?.en || '',
      missing:  !frPair?.value && !frPair?.en,
      editedEN: editsEN[enPair.key] !== undefined,
      editedFR: editsFR[enPair.key] !== undefined,
    };
  });

  const filtered = allPairs
    .filter(p => activeGroup === 'all' || p.group === activeGroup)
    .filter(p => filter === 'all' || (filter === 'missing' && p.missing) || (filter === 'edited' && (p.editedEN || p.editedFR)))
    .filter(p => !search || p.key.includes(search.toLowerCase()) || p.en.toLowerCase().includes(search.toLowerCase()) || p.fr.toLowerCase().includes(search.toLowerCase()));

  const handleSave = () => {
    saveTranslationOverrides('en', editsEN);
    saveTranslationOverrides('fr', editsFR);
    setEditingKey(null);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const editedCount  = allPairs.filter(p => p.editedEN || p.editedFR).length;
  const missingCount = allPairs.filter(p => p.missing).length;

  return (
    <div>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:'1.2rem', gap:'1rem', flexWrap:'wrap' }}>
        <div>
          <h1 style={{ fontFamily:G.serif, fontSize:'2rem', fontWeight:400, color:G.white, marginBottom:'0.2rem' }}>Translations</h1>
          <p style={{ fontSize:'0.72rem', color:G.muted }}>
            Edit EN and FR side by side.{' '}
            <strong style={{color:G.white}}>{allPairs.length}</strong> strings — {' '}
            <span style={{color:G.gold}}>{missingCount} missing FR</span> · {' '}
            <span style={{color:G.green}}>{editedCount} edited</span>
          </p>
        </div>
        <button onClick={handleSave}
          style={{ padding:'0.55rem 1.4rem', background:saved?G.green:G.gold, border:'none', borderRadius:8, color:'#FFFFFF', cursor:'pointer', fontFamily:G.sans, fontSize:'0.75rem', fontWeight:700, flexShrink:0, transition:'background 0.2s' }}>
          {saved ? '✓ Saved!' : 'Save all changes'}
        </button>
      </div>

      {/* Controls */}
      <div style={{ display:'flex', gap:'0.6rem', marginBottom:'1rem', flexWrap:'wrap', alignItems:'center' }}>
        <div style={{ display:'flex', gap:'0.3rem' }}>
          {[['all',`All (${allPairs.length})`],['missing',`Missing FR (${missingCount})`],['edited',`Edited (${editedCount})`]].map(([v,label]) => (
            <button key={v} onClick={() => setFilter(v)}
              style={{ padding:'0.32rem 0.65rem', background:filter===v?G.goldBg:'transparent', border:`1px solid ${filter===v?G.goldBdr:G.border}`, borderRadius:6, color:filter===v?G.gold:G.sub, cursor:'pointer', fontFamily:G.sans, fontSize:'0.65rem', fontWeight:filter===v?600:400, transition:'all 0.15s' }}>
              {label}
            </button>
          ))}
        </div>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search keys or text…"
          style={{ marginLeft:'auto', padding:'0.38rem 0.75rem', background:G.card, border:`1px solid ${G.borderMd}`, borderRadius:8, color:G.text, fontFamily:G.sans, fontSize:'0.72rem', width:220, outline:'none' }}/>
      </div>

      {/* Group pills */}
      <div style={{ display:'flex', gap:'0.35rem', flexWrap:'wrap', marginBottom:'1rem' }}>
        <button onClick={() => setActiveGroup('all')}
          style={{ padding:'0.22rem 0.6rem', background:activeGroup==='all'?G.gold:G.card, border:`1px solid ${activeGroup==='all'?G.gold:G.border}`, borderRadius:99, color:activeGroup==='all'?'#FFFFFF':G.sub, fontFamily:G.sans, fontSize:'0.6rem', fontWeight:600, cursor:'pointer' }}>
          All
        </button>
        {Object.keys(groups).map(grp => (
          <button key={grp} onClick={() => setActiveGroup(grp)}
            style={{ padding:'0.22rem 0.6rem', background:activeGroup===grp?G.gold:G.card, border:`1px solid ${activeGroup===grp?G.gold:G.border}`, borderRadius:99, color:activeGroup===grp?'#FFFFFF':G.sub, fontFamily:G.sans, fontSize:'0.6rem', fontWeight:600, cursor:'pointer', display:'flex', alignItems:'center', gap:'0.3rem' }}>
            {GROUP_LABELS[grp]||grp}
            <span style={{ background:'rgba(0,0,0,0.07)', borderRadius:99, padding:'0 0.28rem', fontSize:'0.54rem' }}>{groups[grp].length}</span>
          </button>
        ))}
      </div>

      {/* Column headers */}
      <div style={{ display:'grid', gridTemplateColumns:'minmax(120px,200px) 1fr 1fr 80px', gap:'0.5rem', padding:'0.4rem 0.7rem', marginBottom:'0.3rem', borderBottom:`1px solid ${G.border}` }}>
        {['Key','English (EN)','French (FR)',''].map((h,i) => (
          <div key={i} style={{ fontSize:'0.58rem', fontWeight:700, color:G.muted, letterSpacing:'0.1em', textTransform:'uppercase' }}>{h}</div>
        ))}
      </div>

      {/* Table rows */}
      <div style={{ display:'flex', flexDirection:'column', gap:'1px', background:G.border, borderRadius:8, overflow:'hidden' }}>
        {filtered.length === 0 && (
          <div style={{ padding:'2rem', textAlign:'center', color:G.muted, fontSize:'0.78rem', background:G.card }}>No strings match your filters.</div>
        )}
        {filtered.map(pair => {
          const isEditing = editingKey === pair.key;
          const currentEN = editsEN[pair.key] ?? pair.en;
          const currentFR = editsFR[pair.key] ?? pair.fr;
          const isEdited  = pair.editedEN || pair.editedFR;

          return (
            <div key={pair.key}
              style={{ display:'grid', gridTemplateColumns:'minmax(120px,200px) 1fr 1fr 80px', gap:'0.5rem', alignItems:'start', padding:'0.55rem 0.7rem', background: isEditing ? G.goldBg : isEdited ? G.greenBg : G.card, borderLeft:`3px solid ${pair.missing?G.gold:isEdited?G.green:'transparent'}`, transition:'background 0.15s' }}>

              {/* Key column */}
              <div style={{ paddingTop:'0.15rem' }}>
                <code style={{ fontSize:'0.58rem', color:G.blue, background:'rgba(91,127,196,0.1)', padding:'0.1rem 0.35rem', borderRadius:4, letterSpacing:'0.02em', wordBreak:'break-all', display:'block', lineHeight:1.6 }}>{pair.key}</code>
                <div style={{ display:'flex', gap:'0.3rem', marginTop:'0.2rem', flexWrap:'wrap' }}>
                  {pair.missing  && <span style={{ fontSize:'0.5rem', background:G.goldBg, color:G.gold,  padding:'0.04rem 0.28rem', borderRadius:99, fontWeight:700, border:`1px solid ${G.goldBdr}` }}>MISSING</span>}
                  {pair.editedEN && <span style={{ fontSize:'0.5rem', background:G.greenBg, color:G.green, padding:'0.04rem 0.28rem', borderRadius:99, fontWeight:700 }}>EN EDITED</span>}
                  {pair.editedFR && <span style={{ fontSize:'0.5rem', background:G.greenBg, color:G.green, padding:'0.04rem 0.28rem', borderRadius:99, fontWeight:700 }}>FR EDITED</span>}
                </div>
              </div>

              {/* EN column */}
              <div>
                {isEditing ? (
                  <textarea
                    value={currentEN}
                    onChange={e => setEditsEN(prev => ({ ...prev, [pair.key]: e.target.value }))}
                    rows={Math.max(1, Math.ceil(currentEN.length / 50))}
                    style={{ width:'100%', background:G.card, border:`1px solid ${G.blue}88`, borderRadius:6, padding:'0.35rem 0.5rem', color:G.text, fontFamily:G.sans, fontSize:'0.75rem', resize:'vertical', lineHeight:1.5, boxSizing:'border-box', outline:'none' }}
                  />
                ) : (
                  <div style={{ fontSize:'0.75rem', color:G.sub, lineHeight:1.55, cursor:'pointer', padding:'0.2rem 0.3rem', borderRadius:4, transition:'background 0.1s' }}
                    onClick={() => setEditingKey(pair.key)}
                    title="Click to edit">
                    {currentEN || <span style={{color:G.muted,fontStyle:'italic'}}>empty</span>}
                  </div>
                )}
              </div>

              {/* FR column */}
              <div>
                {isEditing ? (
                  <textarea
                    value={currentFR}
                    onChange={e => setEditsFR(prev => ({ ...prev, [pair.key]: e.target.value }))}
                    rows={Math.max(1, Math.ceil(Math.max(currentFR.length, 1) / 50))}
                    placeholder={currentEN}
                    style={{ width:'100%', background:G.card, border:`1px solid ${pair.missing?G.gold:G.goldBdr}`, borderRadius:6, padding:'0.35rem 0.5rem', color:G.text, fontFamily:G.sans, fontSize:'0.75rem', resize:'vertical', lineHeight:1.5, boxSizing:'border-box', outline:'none' }}
                  />
                ) : (
                  <div style={{ fontSize:'0.75rem', color: currentFR ? G.sub : G.gold, lineHeight:1.55, cursor:'pointer', padding:'0.2rem 0.3rem', borderRadius:4, fontStyle: currentFR ? 'normal' : 'italic', transition:'background 0.1s' }}
                    onClick={() => setEditingKey(pair.key)}
                    title="Click to edit">
                    {currentFR || '— missing —'}
                  </div>
                )}
              </div>

              {/* Actions column */}
              <div style={{ display:'flex', flexDirection:'column', gap:'0.3rem', paddingTop:'0.1rem' }}>
                {isEditing ? (
                  <>
                    <button onClick={() => { handleSave(); }}
                      style={{ padding:'0.3rem 0.6rem', background:G.green, border:'none', borderRadius:6, color:'#FFFFFF', fontFamily:G.sans, fontSize:'0.62rem', fontWeight:700, cursor:'pointer' }}>
                      ✓ Save
                    </button>
                    <button onClick={() => setEditingKey(null)}
                      style={{ padding:'0.3rem 0.6rem', background:'transparent', border:`1px solid ${G.borderMd}`, borderRadius:6, color:G.sub, fontFamily:G.sans, fontSize:'0.62rem', cursor:'pointer' }}>
                      Cancel
                    </button>
                    {(pair.editedEN || pair.editedFR) && (
                      <button onClick={() => {
                        setEditsEN(prev => { const n={...prev}; delete n[pair.key]; return n; });
                        setEditsFR(prev => { const n={...prev}; delete n[pair.key]; return n; });
                      }}
                        style={{ padding:'0.3rem 0.6rem', background:'transparent', border:`1px solid ${G.borderMd}`, borderRadius:6, color:G.muted, fontFamily:G.sans, fontSize:'0.58rem', cursor:'pointer' }}>
                        ↺ Reset
                      </button>
                    )}
                  </>
                ) : (
                  <button onClick={() => setEditingKey(pair.key)}
                    style={{ padding:'0.3rem 0.7rem', background:'transparent', border:`1px solid ${G.border}`, borderRadius:6, color:G.muted, fontFamily:G.sans, fontSize:'0.65rem', fontWeight:600, cursor:'pointer', transition:'all 0.15s', whiteSpace:'nowrap' }}
                    onMouseEnter={e=>{e.currentTarget.style.borderColor=G.gold;e.currentTarget.style.color=G.gold;}}
                    onMouseLeave={e=>{e.currentTarget.style.borderColor=G.border;e.currentTarget.style.color=G.muted;}}>
                    ✎ Edit
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer note */}
      <div style={{ marginTop:'1.5rem', padding:'0.8rem 1rem', background:'rgba(91,127,196,0.06)', border:`1px solid rgba(91,127,196,0.2)`, borderRadius:8 }}>
        <p style={{ fontSize:'0.68rem', color:G.sub, margin:0, lineHeight:1.6 }}>
          💡 <strong style={{color:G.text}}>Tip:</strong> Click any cell to edit it inline, or click the <strong style={{color:G.text}}>✎ Edit</strong> button to expand both EN and FR at once.
          Placeholders like <code style={{fontSize:'0.62rem',color:G.blue}}>{'{name}'}</code> must be kept in translations.
          Bee wordplay (Bee Loved, Bee Wise) is brand identity — keep as-is.
        </p>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// STATIC PAGES CMS TAB
// ────────────────────────────────────────────────────────────────────────────

// Simple rich-text toolbar + textarea CMS editor
// Admin light-theme palette (matches Admin.js C tokens)
const A = {
  bg:      '#F4F2EF',
  surface: '#FFFFFF',
  border:  '#E5E2DC',
  borderMd:'#D0CCC4',
  text:    '#1A1714',
  sub:     '#5C5650',
  muted:   '#8C8680',
  gold:    '#C9A84C',
  goldBg:  'rgba(201,168,76,0.08)',
  goldBdr: 'rgba(201,168,76,0.35)',
};

function RichEditor({ value, onChange, rows = 6 }) {
  const insert = (before, after = '') => {
    const ta = document.getElementById('cms-editor-active');
    if (!ta) { onChange(value + before + after); return; }
    const s = ta.selectionStart, e = ta.selectionEnd;
    const sel = value.slice(s, e);
    const newVal = value.slice(0, s) + before + (sel || 'text') + after + value.slice(e);
    onChange(newVal);
    setTimeout(() => { ta.focus(); ta.setSelectionRange(s + before.length, s + before.length + (sel||'text').length); }, 0);
  };

  const tools = [
    { label: 'B',    title: 'Bold',     fn: () => insert('**', '**'), style: { fontWeight:700 } },
    { label: 'I',    title: 'Italic',   fn: () => insert('_', '_'),   style: { fontStyle:'italic' } },
    { label: 'H2',   title: 'Heading',  fn: () => insert('\n## ', '') },
    { label: '¶',    title: 'Paragraph',fn: () => insert('\n\n', '') },
    { label: '1.',   title: 'Numbered', fn: () => insert('\n1. ', '') },
    { label: '•',    title: 'Bullet',   fn: () => insert('\n- ', '') },
    { label: '---',  title: 'Divider',  fn: () => insert('\n---\n', '') },
    { label: 'Link', title: 'Link',     fn: () => insert('[', '](url)') },
  ];

  return (
    <div style={{ border:`1px solid ${A.borderMd}`, borderRadius:8, overflow:'hidden' }}>
      {/* Toolbar */}
      <div style={{ display:'flex', gap:0, background:A.bg, borderBottom:`1px solid ${A.border}`, padding:'0.3rem 0.4rem', flexWrap:'wrap' }}>
        {tools.map(tool => (
          <button key={tool.label} onClick={tool.fn} title={tool.title}
            style={{ background:'none', border:'none', color:A.sub, cursor:'pointer', padding:'0.2rem 0.45rem', fontFamily:G.sans, fontSize:'0.7rem', borderRadius:4, ...tool.style }}>
            {tool.label}
          </button>
        ))}
        <div style={{ marginLeft:'auto', fontSize:'0.58rem', color:A.muted, display:'flex', alignItems:'center', paddingRight:'0.4rem' }}>Markdown</div>
      </div>
      {/* Textarea */}
      <textarea
        id="cms-editor-active"
        value={value}
        onChange={e => onChange(e.target.value)}
        rows={rows}
        style={{ width:'100%', background:A.surface, border:'none', padding:'0.7rem 0.85rem', color:A.text, fontFamily:"'Courier New', monospace", fontSize:'0.75rem', resize:'vertical', lineHeight:1.7, boxSizing:'border-box', outline:'none', display:'block' }}
      />
    </div>
  );
}

// Markdown preview renderer
function MarkdownPreview({ text }) {
  if (!text) return <span style={{ color:A.muted, fontStyle:'italic', fontSize:'0.72rem' }}>Nothing to preview</span>;
  const html = text
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/_(.+?)_/g, '<em>$1</em>')
    .replace(/^## (.+)$/gm, `<h3 style="font-family:Georgia,serif;font-size:1rem;color:${A.gold};margin:0.8rem 0 0.4rem;font-weight:400">$1</h3>`)
    .replace(/^### (.+)$/gm, `<h4 style="font-size:0.85rem;color:${A.text};margin:0.6rem 0 0.3rem">$1</h4>`)
    .replace(/^---$/gm, `<hr style="border:none;border-top:1px solid ${A.border};margin:0.8rem 0"/>`)
    .replace(/^\d+\. (.+)$/gm, '<li style="margin:0.2rem 0;padding-left:0.2rem">$1</li>')
    .replace(/^- (.+)$/gm, '<li style="margin:0.2rem 0;list-style:disc;margin-left:1rem">$1</li>')
    .replace(/\[(.+?)\]\((.+?)\)/g, (_, text, url) => {
      const safe = /^(https?:|mailto:)/i.test(url.trim()) ? url.trim() : '#';
      return `<a href="${safe}" style="color:${A.gold}" rel="noopener noreferrer">${text}</a>`;
    })
    .split('\n\n').map(p => p.startsWith('<') ? p : `<p style="margin:0 0 0.8rem;line-height:1.75;font-size:0.82rem">${p}</p>`).join('\n');
  return <div dangerouslySetInnerHTML={{ __html: html }} style={{ color:A.text }}/>;
}

export function StaticPagesTab() {
  const [activePage, setActivePage]   = useState('about');
  const [activeLang, setActiveLang]   = useState('en');
  const [view, setView]               = useState('edit'); // edit | preview | split
  const [saved, setSaved]             = useState(false);

  // Load default content from BASE_PAGES (imported from StaticPage.js)
  const pageDefaults = BASE_PAGES?.[activePage];

  // Build editable flat fields from the page
  const getDefaultFields = (pageId, lang) => {
    const page = BASE_PAGES?.[pageId];
    if (!page) return {};
    const fields = {
      title: page.title?.[lang] || page.title?.en || '',
      subtitle: page.subtitle?.[lang] || page.subtitle?.en || '',
    };
    (page.sections || []).forEach((sec, si) => {
      const prefix = `section_${si}`;
      if (sec.heading) fields[`${prefix}_heading`] = sec.heading[lang] || sec.heading.en || '';
      if (sec.body)    fields[`${prefix}_body`]    = sec.body[lang] || sec.body.en || '';
      if (sec.steps)   sec.steps.forEach((step, sj) => {
        fields[`${prefix}_step_${sj}_title`] = (step[lang]||step.en)?.title || '';
        fields[`${prefix}_step_${sj}_body`]  = (step[lang]||step.en)?.body  || '';
      });
      if (sec.faqs)    sec.faqs.forEach((faq, fj) => {
        fields[`${prefix}_faq_${fj}_q`] = faq.q[lang] || faq.q.en || '';
        fields[`${prefix}_faq_${fj}_a`] = faq.a[lang] || faq.a.en || '';
      });
      if (sec.contacts) sec.contacts.forEach((c, cj) => {
        fields[`${prefix}_contact_${cj}_label`] = c.label[lang] || c.label.en || '';
        fields[`${prefix}_contact_${cj}_value`] = c.value || '';
      });
    });
    return fields;
  };

  const [fields, setFields] = useState(() => {
    const saved = loadPageContent(activePage, activeLang);
    return saved || getDefaultFields(activePage, activeLang);
  });

  const switchPage = (id) => {
    setActivePage(id);
    const saved = loadPageContent(id, activeLang);
    setFields(saved || getDefaultFields(id, activeLang));
  };

  const switchLang = (lang) => {
    setActiveLang(lang);
    const saved = loadPageContent(activePage, lang);
    setFields(saved || getDefaultFields(activePage, lang));
  };

  const handleSave = () => {
    savePageContent(activePage, activeLang, fields);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handleReset = () => {
    resetPageContent(activePage, activeLang);
    setFields(getDefaultFields(activePage, activeLang));
  };

  // Build field groups for rendering
  const page = BASE_PAGES?.[activePage];
  const fieldGroups = [];
  if (page) {
    fieldGroups.push({ label: 'Page header', fields: [
      { key:'title',    label:'Page title',   type:'text'  },
      { key:'subtitle', label:'Subtitle',     type:'text'  },
    ]});
    (page.sections||[]).forEach((sec, si) => {
      const prefix = `section_${si}`;
      const grpLabel = sec.heading?.en || `Section ${si+1}`;
      const grpFields = [];
      if (sec.heading) grpFields.push({ key:`${prefix}_heading`, label:'Section heading', type:'text' });
      if (sec.body)    grpFields.push({ key:`${prefix}_body`,    label:'Body content',    type:'rich' });
      if (sec.steps)   sec.steps.forEach((step, sj) => {
        grpFields.push({ key:`${prefix}_step_${sj}_title`, label:`Step ${step.n||sj+1} — title`, type:'text' });
        grpFields.push({ key:`${prefix}_step_${sj}_body`,  label:`Step ${step.n||sj+1} — body`,  type:'rich', rows:3 });
      });
      if (sec.faqs)    sec.faqs.forEach((faq, fj) => {
        grpFields.push({ key:`${prefix}_faq_${fj}_q`, label:`FAQ ${fj+1} — question`, type:'text' });
        grpFields.push({ key:`${prefix}_faq_${fj}_a`, label:`FAQ ${fj+1} — answer`,   type:'rich', rows:3 });
      });
      if (sec.contacts) sec.contacts.forEach((c, cj) => {
        grpFields.push({ key:`${prefix}_contact_${cj}_label`, label:`Contact ${cj+1} — label`, type:'text' });
        grpFields.push({ key:`${prefix}_contact_${cj}_value`, label:`Contact ${cj+1} — email`, type:'text' });
      });
      fieldGroups.push({ label: grpLabel, fields: grpFields });
    });
  }

  // Full markdown preview of the whole page
  const fullPreview = fieldGroups.map(grp =>
    `## ${grp.label}\n\n` + grp.fields.filter(f=>f.type==='rich').map(f=>fields[f.key]||'').join('\n\n')
  ).join('\n\n---\n\n');

  const hasCustom = !!loadPageContent(activePage, activeLang);

  return (
    <div>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:'1.5rem', gap:'1rem', flexWrap:'wrap' }}>
        <div>
          <h1 style={{ fontFamily:G.serif, fontSize:'2rem', fontWeight:400, color:A.text, marginBottom:'0.2rem' }}>Static Pages</h1>
          <p style={{ fontSize:'0.72rem', color:A.muted }}>Full CMS editor for all public-facing pages. Supports Markdown formatting.</p>
        </div>
        <div style={{ display:'flex', gap:'0.5rem', flexShrink:0 }}>
          {hasCustom && (
            <button onClick={handleReset}
              style={{ padding:'0.45rem 0.9rem', background:'transparent', border:`1px solid rgba(224,112,112,0.4)`, borderRadius:8, color:G.red, cursor:'pointer', fontFamily:G.sans, fontSize:'0.72rem' }}>
              ↺ Reset to default
            </button>
          )}
          <button onClick={handleSave}
            style={{ padding:'0.45rem 1.3rem', background:saved?G.green:G.gold, border:'none', borderRadius:8, color:'#0F0D1A', cursor:'pointer', fontFamily:G.sans, fontSize:'0.72rem', fontWeight:700, transition:'background 0.2s' }}>
            {saved ? '✓ Saved!' : 'Save page'}
          </button>
        </div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'220px 1fr', gap:'1.5rem', alignItems:'start' }}>
        {/* Left: page + lang selectors */}
        <div>
          {/* Language */}
          <div style={{ marginBottom:'1rem' }}>
            <div style={{ fontSize:'0.55rem', letterSpacing:'0.1em', textTransform:'uppercase', color:'#8C8680', marginBottom:'0.4rem', fontFamily:G.sans }}>Language</div>
            <div style={{ display:'flex', gap:'0.3rem' }}>
              {SUPPORTED_LANGS.map(l => (
                <button key={l.code} onClick={() => switchLang(l.code)}
                  style={{ flex:1, padding:'0.35rem', background:activeLang===l.code?G.gold:'transparent', border:`1px solid ${activeLang===l.code?G.gold:'#E5E2DC'}`, borderRadius:6, color:activeLang===l.code?'#1A1714':'#5C5650', cursor:'pointer', fontFamily:G.sans, fontSize:'0.7rem', fontWeight:700 }}>
                  {l.code.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          {/* Pages */}
          <div style={{ fontSize:'0.55rem', letterSpacing:'0.1em', textTransform:'uppercase', color:'#8C8680', marginBottom:'0.4rem', fontFamily:G.sans }}>Page</div>
          {CMS_PAGES.map(p => {
            const hasCust = !!loadPageContent(p.id, activeLang);
            const isActive = activePage === p.id;
            return (
              <button key={p.id} onClick={() => switchPage(p.id)}
                style={{ width:'100%', display:'flex', alignItems:'center', gap:'0.5rem', padding:'0.55rem 0.7rem', background:isActive?'rgba(201,168,76,0.1)':'transparent', border:`1px solid ${isActive?'rgba(201,168,76,0.5)':'#E5E2DC'}`, borderRadius:8, cursor:'pointer', marginBottom:'0.3rem', textAlign:'left' }}>
                <span style={{ fontSize:'0.9rem' }}>{p.icon}</span>
                <span style={{ fontSize:'0.72rem', color:isActive?G.gold:'#1A1714', fontFamily:G.sans, flex:1, fontWeight:isActive?600:400 }}>{p.label}</span>
                {hasCust && <span style={{ width:6, height:6, borderRadius:'50%', background:'#1A7A4A', flexShrink:0 }} title="Custom content saved"/>}
              </button>
            );
          })}

          {/* View toggle */}
          <div style={{ marginTop:'1rem', fontSize:'0.55rem', letterSpacing:'0.1em', textTransform:'uppercase', color:'#8C8680', marginBottom:'0.4rem', fontFamily:G.sans }}>View</div>
          <div style={{ display:'flex', gap:'0.3rem' }}>
            {[['edit','Edit'],['split','Split'],['preview','Preview']].map(([v,label]) => (
              <button key={v} onClick={() => setView(v)}
                style={{ flex:1, padding:'0.3rem', background:view===v?'rgba(201,168,76,0.1)':'transparent', border:`1px solid ${view===v?'rgba(201,168,76,0.4)':'#E5E2DC'}`, borderRadius:6, color:view===v?G.gold:'#5C5650', cursor:'pointer', fontFamily:G.sans, fontSize:'0.62rem', fontWeight:view===v?600:400 }}>
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Right: editor */}
        <div>
          {view === 'edit' && (
            <div style={{ display:'flex', flexDirection:'column', gap:'1.2rem' }}>
              {fieldGroups.map((grp, gi) => (
                <div key={gi} style={{ background:A.surface, border:`1px solid ${A.border}`, borderRadius:10, overflow:'hidden' }}>
                  <div style={{ padding:'0.7rem 1rem', borderBottom:`1px solid ${A.border}`, background:A.bg }}>
                    <span style={{ fontSize:'0.65rem', fontWeight:700, color:A.sub, fontFamily:G.sans, letterSpacing:'0.04em', textTransform:'uppercase' }}>{grp.label}</span>
                  </div>
                  <div style={{ padding:'1rem', display:'flex', flexDirection:'column', gap:'0.85rem' }}>
                    {grp.fields.map(field => (
                      <div key={field.key}>
                        <label style={{ fontSize:'0.58rem', color:A.muted, display:'block', marginBottom:'0.3rem', letterSpacing:'0.08em', textTransform:'uppercase', fontFamily:G.sans }}>
                          {field.label}
                          <code style={{ marginLeft:'0.4rem', fontSize:'0.56rem', color:'#5B7FC4', background:'rgba(91,127,196,0.08)', padding:'0.02rem 0.28rem', borderRadius:3, textTransform:'none', letterSpacing:0 }}>{field.key}</code>
                        </label>
                        {field.type === 'rich' ? (
                          <RichEditor value={fields[field.key]||''} onChange={v => setFields(f=>({...f,[field.key]:v}))} rows={field.rows||6}/>
                        ) : (
                          <input value={fields[field.key]||''} onChange={e => setFields(f=>({...f,[field.key]:e.target.value}))}
                            style={{ width:'100%', background:A.bg, border:`1px solid ${A.borderMd}`, borderRadius:8, padding:'0.5rem 0.7rem', color:A.text, fontFamily:G.sans, fontSize:'0.82rem', boxSizing:'border-box', outline:'none' }}/>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {view === 'preview' && (
            <div style={{ background:A.surface, border:`1px solid ${A.border}`, borderRadius:10, padding:'1.5rem' }}>
              <div style={{ fontFamily:G.serif, fontSize:'1.6rem', color:A.gold, marginBottom:'0.3rem' }}>{fields.title}</div>
              <div style={{ fontSize:'0.82rem', color:A.muted, marginBottom:'1.2rem', lineHeight:1.6 }}>{fields.subtitle}</div>
              <div style={{ height:1, background:`linear-gradient(90deg, ${A.goldBdr}, transparent)`, marginBottom:'1.2rem' }}/>
              {fieldGroups.slice(1).map((grp, gi) => (
                <div key={gi} style={{ marginBottom:'1.5rem' }}>
                  <div style={{ fontFamily:G.serif, fontSize:'1.1rem', color:A.gold, marginBottom:'0.7rem' }}>{fields[`section_${gi}_heading`]||grp.label}</div>
                  {grp.fields.filter(f=>f.type==='rich').map(f => (
                    <MarkdownPreview key={f.key} text={fields[f.key]||''}/>
                  ))}
                </div>
              ))}
            </div>
          )}

          {view === 'split' && (
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1rem', alignItems:'start' }}>
              <div style={{ display:'flex', flexDirection:'column', gap:'0.8rem' }}>
                {fieldGroups.map((grp, gi) => (
                  <div key={gi}>
                    <div style={{ fontSize:'0.58rem', color:A.muted, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'0.4rem', fontFamily:G.sans }}>{grp.label}</div>
                    {grp.fields.map(field => (
                      <div key={field.key} style={{ marginBottom:'0.6rem' }}>
                        <div style={{ fontSize:'0.56rem', color:A.muted, marginBottom:'0.15rem', fontFamily:G.sans }}>{field.label}</div>
                        {field.type === 'rich' ? (
                          <RichEditor value={fields[field.key]||''} onChange={v => setFields(f=>({...f,[field.key]:v}))} rows={3}/>
                        ) : (
                          <input value={fields[field.key]||''} onChange={e => setFields(f=>({...f,[field.key]:e.target.value}))}
                            style={{ width:'100%', background:A.bg, border:`1px solid ${A.borderMd}`, borderRadius:6, padding:'0.4rem 0.6rem', color:A.text, fontFamily:G.sans, fontSize:'0.75rem', boxSizing:'border-box', outline:'none' }}/>
                        )}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
              <div style={{ position:'sticky', top:20, background:A.surface, border:`1px solid ${A.border}`, borderRadius:10, padding:'1.2rem', maxHeight:'80vh', overflowY:'auto' }}>
                <div style={{ fontSize:'0.55rem', color:A.muted, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'0.8rem', fontFamily:G.sans }}>Live preview</div>
                <div style={{ fontFamily:G.serif, fontSize:'1.3rem', color:A.gold, marginBottom:'0.2rem' }}>{fields.title}</div>
                <div style={{ fontSize:'0.75rem', color:A.muted, marginBottom:'1rem' }}>{fields.subtitle}</div>
                {fieldGroups.slice(1).map((grp, gi) => (
                  <div key={gi} style={{ marginBottom:'1.2rem' }}>
                    <div style={{ fontFamily:G.serif, fontSize:'0.9rem', color:A.gold, marginBottom:'0.5rem' }}>{fields[`section_${gi}_heading`]||grp.label}</div>
                    {grp.fields.filter(f=>f.type==='rich').map(f => <MarkdownPreview key={f.key} text={fields[f.key]||''}/>)}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
